<template>
  <v-dialog
    v-model="showDialog"
    max-width="600"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card>
      <v-card-title class="justify-center text-uppercase headline">
        Create pick order
      </v-card-title>
      <v-divider />
      <div class="px-10 pb-5">
        <v-form ref="pick-order-form">
          <ant-input label="Type">
            <template #input-field>
              <v-select
                v-model="pickOrderItem.options.custom_1"
                :items="['Portalen', 'Wanden', 'Vloeren', 'Modules']"
                :rules="[rules.required]"
                dense
                filled
                hide-details
                placeholder="Type"
                single-line
                type="text"
              />
            </template>
          </ant-input>

          <ant-input label="Project">
            <template #input-field>
              <v-select
                v-model="pickOrderItem.project"
                :items="projects"
                :rules="[rules.required]"
                dense
                filled
                hide-details
                item-text="name"
                item-value="id"
                placeholder="Project"
                single-line
                type="text"
              />
            </template>
          </ant-input>

          <ant-input label="Date">
            <template #input-field>
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                min-width="auto"
                offset-y
                transition="scale-transition"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="pickOrderItem.due"
                    :rules="[rules.required]"
                    clearable
                    dense
                    filled
                    hide-details
                    placeholder="Date"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    single-line
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="pickOrderItem.due"
                  :min="now"
                  @input="dateMenu = false"
                />
              </v-menu>
            </template>
          </ant-input>

          <ant-input label="Id">
            <template #input-field>
              <v-combobox
                v-model="pickOrderItem.options.custom_2"
                :rules="[rules.required]"
                deletable-chips
                dense
                filled
                hide-details
                multiple
                placeholder="ID"
                single-line
                small-chips
                type="text"
              />
            </template>
          </ant-input>

          <div class="d-flex">
            <ant-input label="From">
              <template #input-field>
                <v-select
                  v-model="pickOrderItem.options.custom_3"
                  :items="locations"
                  :rules="[rules.required]"
                  append-outer-icon="mdi-arrow-expand-right"
                  dense
                  filled
                  hide-details
                  item-value="id"
                  placeholder="From"
                  single-line
                >
                  <template #selection="{ item }">
                    <v-icon class="mr-2">
                      {{
                        isAssemblyHall(item)
                          ? 'mdi-warehouse'
                          : 'mdi-file-table-box'
                      }}
                    </v-icon>
                    {{ isAssemblyHall(item) ? item.hall : item.name }}
                  </template>
                  <template #item="{ item }">
                    <div class="d-flex align-center">
                      <v-icon class="mr-2">
                        {{
                          isAssemblyHall(item)
                            ? 'mdi-warehouse'
                            : 'mdi-file-table-box'
                        }}
                      </v-icon>
                      {{ isAssemblyHall(item) ? item.hall : item.name }}
                    </div>
                  </template>
                </v-select>
              </template>
            </ant-input>
            <v-spacer />

            <ant-input label="To">
              <template #input-field>
                <v-select
                  v-model="pickOrderItem.options.custom_4"
                  :items="locations"
                  :rules="[rules.required]"
                  dense
                  filled
                  hide-details
                  item-value="id"
                  placeholder="To"
                  single-line
                >
                  <template #selection="{ item }">
                    <v-icon class="mr-2">
                      {{
                        isAssemblyHall(item)
                          ? 'mdi-warehouse'
                          : 'mdi-file-table-box'
                      }}
                    </v-icon>
                    {{ isAssemblyHall(item) ? item.hall : item.name }}
                  </template>
                  <template #item="{ item }">
                    <div class="d-flex align-center">
                      <v-icon class="mr-2">
                        {{
                          isAssemblyHall(item)
                            ? 'mdi-warehouse'
                            : 'mdi-file-table-box'
                        }}
                      </v-icon>
                      {{ isAssemblyHall(item) ? item.hall : item.name }}
                    </div>
                  </template>
                </v-select>
              </template>
            </ant-input>
          </div>

          <ant-input label="Description">
            <template #input-field>
              <v-text-field
                v-model="pickOrderItem.description"
                :items="locations"
                :rules="[rules.required]"
                dense
                filled
                hide-details
                placeholder="Description"
                single-line
              />
            </template>
          </ant-input>
        </v-form>
      </div>

      <v-card-actions class="ant-dialog-actions-bg ant-border-top">
        <v-spacer />
        <v-btn
          :disabled="$wait.is('dhme.pick-order-management.create')"
          color="error"
          small
          text
          @click="closeDialog"
        >
          Cancel
        </v-btn>
        <v-btn
          :loading="$wait.is('dhme.pick-order-management.create')"
          color="primary"
          elevation="0"
          small
          @click="createPickOrder"
        >
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AntInput from '@/components/AntInput.vue';
import moment from 'moment';
import { executeCustomModuleCall } from '@/services/api/module.api';
import { mapGetters } from 'vuex';
import { DHME_M_PICK_ORDER_MANAGEMENT } from '@/modules/modules';
import PickOrderManagementMixin from '@/components/Modules/Daiwa-House-Modular-Europe/PickOrderAndYardManagement/PickOrderManagementMixin';
import InputRulesMixin from '@/Mixins/InputRulesMixin';

export default {
  name: 'AddPickOrderDialog',
  components: { AntInput },
  mixins: [PickOrderManagementMixin, InputRulesMixin],
  props: {
    displayed: {
      type: Boolean,
      required: true,
    },
  },
  data: () => {
    return {
      pickOrderItem: {
        options: {
          type: 'dhme-pickorder',
        },
      },
      dateMenu: false,
      now: moment().format('YYYY-MM-DD'),
      projects: [],
    };
  },
  computed: {
    ...mapGetters([
      'authenticatedUser',
      'project',
      'dhmePickOrderManagementAssemblyHalls',
      'dhmePickOrderManagementStorageLocations',
    ]),
    moduleId() {
      return this.project.modules.find(
        (module) => module.route === DHME_M_PICK_ORDER_MANAGEMENT
      ).id;
    },
    showDialog() {
      return this.displayed;
    },
  },
  async mounted() {
    this.projects = await executeCustomModuleCall(
      this.project.id,
      this.moduleId,
      'fetchProjects'
    );
  },
  methods: {
    closeDialog() {
      this.pickOrderItem = Object.assign(
        {},
        {
          options: {
            type: 'dhme-pickorder',
          },
        }
      );
      this.$refs['pick-order-form'].reset();
      this.$emit('closeDialog');
    },
    async createPickOrder() {
      if (this.$refs['pick-order-form'].validate()) {
        this.pickOrderItem.license = this.project.license;
        this.pickOrderItem.project = this.project.id;
        this.pickOrderItem.assigned_to = this.authenticatedUser.id;
        this.pickOrderItem.title = `Pickorder: ${this.pickOrderItem.options.custom_2}`;
        this.pickOrderItem.priority = 'normal';

        let tasks = [];

        this.pickOrderItem.options.custom_2.forEach((id) => {
          let object = JSON.parse(JSON.stringify(this.pickOrderItem));
          object.license = this.project.license;
          object.project = this.project.id;
          object.assigned_to = this.authenticatedUser.id;
          object.title = `Pickorder: ${id}`;
          object.priority = 'normal';
          object.options.custom_2 = id;
          tasks.push(object);
        });

        try {
          await this.$store.dispatch('createPickOrderTasks', tasks);
          this.closeDialog();
          await this.$store.dispatch('updatePickOrderTasks', {
            moduleId: this.moduleId,
          });
        } catch (e) {}
      }
    },
  },
};
</script>

<style scoped></style>
